import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Building2, CreditCard, CheckCircle, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchisePaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appId = searchParams.get('app_id');
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [application, setApplication] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [franchiseFee, setFranchiseFee] = useState(50000);

  useEffect(() => {
    if (!appId) {
      toast.error('Invalid payment link');
      navigate('/');
      return;
    }
    fetchApplication();
  }, [appId]);

  const fetchApplication = async () => {
    try {
      // Try to get application details (this is a public endpoint for approved apps)
      const response = await axios.get(`${API}/franchise-applications/${appId}`);
      setApplication(response.data);
      
      if (response.data.status !== 'approved') {
        if (response.data.status === 'active') {
          toast.info('This franchise is already activated');
        } else {
          toast.error('This application is not approved yet');
        }
      }
      
      // Get franchise fee
      try {
        const settingsRes = await axios.get(`${API}/franchise-settings`);
        setFranchiseFee(settingsRes.data.franchise_fee || 50000);
      } catch (e) {
        // Use default
      }
    } catch (error) {
      toast.error('Invalid or expired payment link');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      // Create order
      const orderRes = await axios.post(`${API}/franchise/create-payment-order?app_id=${appId}`);
      const { order_id, amount, key_id } = orderRes.data;
      
      // Open Razorpay
      const options = {
        key: key_id,
        amount: amount * 100,
        currency: 'INR',
        name: 'IqraSkill',
        description: 'Franchise Fee Payment',
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyRes = await axios.post(`${API}/franchise/verify-payment`, null, {
              params: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                app_id: appId
              }
            });
            
            setCredentials({
              center_code: verifyRes.data.center_code,
              email: verifyRes.data.login_email,
              password: verifyRes.data.temp_password
            });
            setPaymentSuccess(true);
            toast.success('Payment successful! Your center is now active.');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: application?.owner_name,
          email: application?.email,
          contact: application?.mobile
        },
        theme: {
          color: '#2563eb'
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (paymentSuccess && credentials) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Link to="/" className="text-2xl font-bold">
              <span className="text-blue-600">Iqra</span>
              <span className="text-emerald-600">skill</span>
            </Link>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">
              Congratulations! Your Center is Active
            </h1>
            <p className="text-lg text-slate-600 mb-6">
              Your franchise center has been successfully activated.
            </p>
            
            {/* Credentials Box */}
            <div className="bg-blue-50 rounded-xl p-6 mb-6 text-left">
              <h3 className="font-semibold text-blue-800 mb-4 text-center">Your Login Credentials</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center bg-white rounded-lg p-3">
                  <span className="text-slate-600">Center Code:</span>
                  <span className="font-mono font-bold text-blue-600">{credentials.center_code}</span>
                </div>
                <div className="flex justify-between items-center bg-white rounded-lg p-3">
                  <span className="text-slate-600">Login Email:</span>
                  <span className="font-medium">{credentials.email}</span>
                </div>
                <div className="flex justify-between items-center bg-white rounded-lg p-3">
                  <span className="text-slate-600">Password:</span>
                  <span className="font-mono font-bold text-emerald-600">{credentials.password}</span>
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-4 text-center">
                ⚠️ Please save these credentials. You can change your password after logging in.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/center/login">
                <Button className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                  <Building2 className="w-4 h-4 mr-2" />
                  Go to Center Login
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full sm:w-auto">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold">
            <span className="text-blue-600">Iqra</span>
            <span className="text-emerald-600">skill</span>
          </Link>
          <Link to="/" className="text-slate-600 hover:text-blue-600 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Franchise Fee Payment</h1>
            <p className="text-blue-100">Complete your payment to activate your franchise center</p>
          </div>

          {application?.status !== 'approved' ? (
            <div className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">
                {application?.status === 'active' ? 'Already Activated' : 'Not Ready for Payment'}
              </h2>
              <p className="text-slate-600 mb-6">
                {application?.status === 'active' 
                  ? 'This franchise center is already active. Please login to your dashboard.'
                  : 'Your application needs to be approved before payment.'}
              </p>
              <Link to={application?.status === 'active' ? '/center/login' : '/'}>
                <Button>
                  {application?.status === 'active' ? 'Go to Login' : 'Back to Home'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="p-8">
              {/* Application Details */}
              <div className="mb-8">
                <h3 className="font-semibold text-slate-800 mb-4">Application Details</h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Center Name:</span>
                    <span className="font-medium">{application.center_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Owner:</span>
                    <span className="font-medium">{application.owner_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Center Code:</span>
                    <span className="font-mono font-bold text-blue-600">{application.center_code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Location:</span>
                    <span className="font-medium">{application.city}, {application.state}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="mb-8">
                <h3 className="font-semibold text-slate-800 mb-4">Payment Details</h3>
                <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-slate-700">Franchise Fee</span>
                    <span className="text-2xl font-bold text-blue-600">₹{franchiseFee.toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p>✓ Complete training and support</p>
                    <p>✓ Marketing materials</p>
                    <p>✓ Student management system access</p>
                    <p>✓ Brand authorization</p>
                  </div>
                </div>
              </div>

              {/* Pay Button */}
              <Button
                onClick={handlePayment}
                disabled={processing}
                className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-lg py-6"
                data-testid="pay-franchise-fee-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ₹{franchiseFee.toLocaleString()}
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-slate-500 mt-4">
                Secure payment powered by Razorpay
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FranchisePaymentPage;
